import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Instagram } from 'lucide-react';

const ApexFooter = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleEmailClick = () => {
    window.location.href = 'mailto:kyran@apexfitsalescoaching.com';
  };

  const handleSectionClick = (sectionId) => {
    // Navigate to homepage with the section hash
    navigate('/#' + sectionId);
    
    // After navigation, scroll to the section
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-1 space-y-6">
            <div className="flex items-center gap-3">
              <img
                src="/apex-icon.png"
                alt="Apex Fit Sales Coaching"
                className="h-12 w-12 object-contain"
              />
              <div>
                <span className="text-white font-bold text-lg block">Apex Growth</span>
                <span className="text-[#71717A] text-sm">Sales Coaching</span>
              </div>
            </div>
            <p className="text-[#A1A1AA] max-w-md">
              Transforming gym owners from fearing sales to mastering them. 
              7,500+ consultations. 28% average improvement. One partner per area.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={handleEmailClick}
                className="w-10 h-10 rounded-full bg-[#121212] border border-[#27272A] flex items-center justify-center hover:border-[#22C55E] transition-colors group"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#22C55E]" />
              </button>
              <a
                href="https://instagram.com/apexfitsales"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#121212] border border-[#27272A] flex items-center justify-center hover:border-[#22C55E] transition-colors group"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#22C55E]" />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => handleSectionClick('how-it-works')} 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors text-left"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleSectionClick('benefits')} 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors text-left"
                >
                  Benefits
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleSectionClick('calculator')} 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors text-left"
                >
                  Revenue Calculator
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleSectionClick('results')} 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors text-left"
                >
                  Success Stories
                </button>
              </li>
              <li>
                <Link 
                  to="/5k-guarantee" 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
                >
                  £5K Guarantee
                </Link>
              </li>
              <li>
                <Link 
                  to="/pt-growth-academy" 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
                >
                  PT Growth Academy
                </Link>
              </li>
              <li>
                <Link 
                  to="/lead-conversion" 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
                >
                  Lead Conversion
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#22C55E]" />
                <a 
                  href="mailto:kyran@apexfitsalescoaching.com" 
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
                >
                  kyran@apexfitsalescoaching.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-[#22C55E]" />
                <a 
                  href="https://instagram.com/apexfitsales"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#A1A1AA] hover:text-[#22C55E] transition-colors"
                >
                  @apexfitsales
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#27272A] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#71717A] text-sm">
            © {currentYear} Apex Fit Sales Coaching. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-[#71717A] hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#71717A] hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ApexFooter;
